import { MeasureController } from "../../MeasureController";
import { JudgmentResult, JudgmentResultType } from "../../types.client";
import { ClockLineVoltageMeasure } from "../../../models/schemas/measures/electrical.schema";
import { CLOCK_LINE_VOLTAGE_THRESHOLDS } from "../../constants/judgmentCriteria";

/**
 * 전자시계 회선별 전압 컨트롤러
 * 판정 기준 (송출방식별, ±10%) - CLOCK_LINE_VOLTAGE_THRESHOLDS 사용:
 * - DC12V: 10.8 <= 측정값 <= 13.2V
 * - DC24V: 21.6 <= 측정값 <= 26.4V
 * - AC24V: 21.6 <= 측정값 <= 26.4V
 * - AC220V: 198 <= 측정값 <= 242V
 */
export class ClockLineVoltageController extends MeasureController<ClockLineVoltageMeasure> {
  protected getDefaultData(): ClockLineVoltageMeasure {
    return {
      transmissionMethod: "",
      measuredValue: 0,
      result: null,
      discussion: "",
      remark: "",
      photo: { checkUrl: "", resultUrl: "" },
    };
  }

  protected judge(): JudgmentResult {
    const { transmissionMethod, measuredValue } = this.data;

    // 데이터 미입력 시
    if (!transmissionMethod || !measuredValue) {
      return {
        result: "NA",
        discussion: this.generateDiscussion("NA"),
      };
    }

    // 송출방식별 전압 범위 체크
    let result: JudgmentResultType;
    const method = transmissionMethod.toUpperCase();

    if (method.includes("DC12V") || method === "DC12V") {
      // DC12V: 공유 상수 사용
      const { min, max } = CLOCK_LINE_VOLTAGE_THRESHOLDS.DC12V;
      result = measuredValue >= min && measuredValue <= max ? "GOOD" : "BAD";
    } else if (method.includes("DC24V") || method === "DC24V") {
      // DC24V: 공유 상수 사용
      const { min, max } = CLOCK_LINE_VOLTAGE_THRESHOLDS.DC24V;
      result = measuredValue >= min && measuredValue <= max ? "GOOD" : "BAD";
    } else if (method.includes("AC24V") || method === "AC24V") {
      // AC24V: 공유 상수 사용
      const { min, max } = CLOCK_LINE_VOLTAGE_THRESHOLDS.AC24V;
      result = measuredValue >= min && measuredValue <= max ? "GOOD" : "BAD";
    } else if (method.includes("AC220V") || method === "AC220V") {
      // AC220V: 공유 상수 사용
      const { min, max } = CLOCK_LINE_VOLTAGE_THRESHOLDS.AC220V;
      result = measuredValue >= min && measuredValue <= max ? "GOOD" : "BAD";
    } else {
      // 알 수 없는 송출방식
      return {
        result: "NA",
        discussion: "알 수 없는 송출방식",
      };
    }

    return {
      result,
      discussion: this.generateDiscussion(result),
    };
  }

  protected generateDiscussion(result: JudgmentResultType): string {
    const { transmissionMethod } = this.data;

    if (result === "NA") {
      return "데이터 미입력";
    }

    const method = transmissionMethod?.toUpperCase() || "";
    if (result === "GOOD") {
      if (method.includes("DC12V")) {
        return "DC12V 회선 전압이 10.8~13.2V 범위 내로 정상";
      }
      if (method.includes("DC24V")) {
        return "DC24V 회선 전압이 21.6~26.4V 범위 내로 정상";
      }
      if (method.includes("AC24V")) {
        return "AC24V 회선 전압이 21.6~26.4V 범위 내로 정상";
      }
      if (method.includes("AC220V")) {
        return "AC220V 회선 전압이 198~242V 범위 내로 정상";
      }
      return "회선 전압 정상";
    }

    if (method.includes("DC12V")) {
      return "DC12V 회선 전압이 10.8~13.2V 범위를 벗어남. 점검 필요";
    }
    if (method.includes("DC24V")) {
      return "DC24V 회선 전압이 21.6~26.4V 범위를 벗어남. 점검 필요";
    }
    if (method.includes("AC24V")) {
      return "AC24V 회선 전압이 21.6~26.4V 범위를 벗어남. 점검 필요";
    }
    if (method.includes("AC220V")) {
      return "AC220V 회선 전압이 198~242V 범위를 벗어남. 점검 필요";
    }
    return "회선 전압이 기준 범위를 벗어남. 점검 필요";
  }
}
